// src/pages/Estoque/venda/DashboardVendas.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  useMediaQuery,
  Container,
  Popover,
  Button,
  IconButton,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { VendasService, IVendedorDashboardVendas } from '../../../shared/services/api/estoque/venda/venda';

dayjs.locale('pt-br');
dayjs.extend(utc);

const MESES_ABREVIADOS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

interface VendaDia {
  dia: string;
  valor: number;
}

interface TipoVendaItem {
  name: string;
  value: number;
  color: string;
}

export const DashboardVendas: React.FC = () => {
  const theme = useTheme();
  const [msgType, setMsgType] = useState('');
  const [msg, setMsg] = useState('');
  const [openMsg, setOpenMsg] = useState(false);
  const isMobile = useMediaQuery(
    theme.breakpoints.down('sm')
  );

  const [vendedores, setVendedores] = useState<IVendedorDashboardVendas[]>([]);
  const [dadosRosca, setDadosRosca] = useState<TipoVendaItem[]>([]);
  const [dadosDiarios, setDadosDiarios] = useState<VendaDia[]>([]);

  const [mesSelecionado, setMesSelecionado] = useState(
    dayjs().format('YYYY-MM')
  );

  const [anchorMes, setAnchorMes] = useState<HTMLElement | null>(null);
  const [anoPopover, setAnoPopover] = useState(dayjs().year());

  const abrirSeletorMes = (target: HTMLElement) => {
    setAnoPopover(Number(mesSelecionado.split('-')[0]));
    setAnchorMes(target);
  };

  const mesFormatado = (() => {
    const texto = dayjs(`${mesSelecionado}-01`).format('MMMM [de] YYYY');
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  })();

  const [totais, setTotais] = useState({
    vendas: 0,
    total: 0,
    comissao: 0,
  });

  useEffect(() => {

    const carregarDashboard = async () => {

      const inicioDoMes = dayjs(`${mesSelecionado}-01`).startOf('month');
      const fimDoMes = dayjs(`${mesSelecionado}-01`).endOf('month');

      const result = await VendasService.getDashboardVendas(
        inicioDoMes.format('DD/MM/YYYY'),
        fimDoMes.format('DD/MM/YYYY'),
      );

      if (result instanceof Error) {

        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);

      } else {

        const listaVendedores = (result.vendedores || []).map((item) => ({
          ...item,
          vendas: item.vendas || '0',
          total: Number(item.total || 0),
          comissao: Number(item.comissao || 0),
        }));

        setVendedores(listaVendedores);

        setTotais({
          vendas: listaVendedores.reduce((acc, item) => acc + Number(item.vendas || 0), 0),
          total: listaVendedores.reduce((acc, item) => acc + Number(item.total || 0), 0),
          comissao: listaVendedores.reduce((acc, item) => acc + Number(item.comissao || 0), 0),
        });

        setDadosRosca([
          {
            name: 'À Vista',
            value: Number(result.tipovenda?.avista || 0),
            color: theme.palette.success.main,
          },
          {
            name: 'A Prazo',
            value: Number(result.tipovenda?.aprazo || 0),
            color: theme.palette.warning.main,
          },
        ]);

        const dadosDia: VendaDia[] = (result.grafico || []).map((item) => ({
          dia: dayjs.utc(item.emissao).format('DD/MM'),
          valor: Number(item.valor || 0),
        }));

        setDadosDiarios(dadosDia);
      }
    };

    carregarDashboard();

  }, [mesSelecionado]);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  return (
    <LayoutBaseDeDashboard
      barraDeFerramentas={<Box />}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          {/* CARDS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              mb: 2,
            }}
          >
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                onClick={(e) => abrirSeletorMes(e.currentTarget)}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Período
                      </Typography>

                      <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {mesFormatado}
                        </Typography>

                        <ArrowDropDownIcon
                          sx={{ color: theme.palette.info.main }}
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.info.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Popover
                open={Boolean(anchorMes)}
                anchorEl={anchorMes}
                onClose={() => setAnchorMes(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ width: 240, p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((ano) => ano - 1)}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="subtitle2" fontWeight="bold">
                      {anoPopover}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((ano) => ano + 1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Grid container spacing={1}>
                    {MESES_ABREVIADOS.map((nomeMes, index) => {
                      const valorMes = `${anoPopover}-${String(index + 1).padStart(2, '0')}`;
                      const selecionado = valorMes === mesSelecionado;

                      return (
                        <Grid item xs={4} key={nomeMes}>
                          <Button
                            fullWidth
                            size="small"
                            variant={selecionado ? 'contained' : 'text'}
                            color="primary"
                            onClick={() => {
                              setMesSelecionado(valorMes);
                              setAnchorMes(null);
                            }}
                            sx={{ minWidth: 0, px: 0 }}
                          >
                            {nomeMes}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Popover>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total de Vendas
                      </Typography>

                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ mt: 1 }}
                      >
                        {totais.vendas}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <ShoppingCartIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.primary.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Valor Total
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.success.main }}
                      >
                        {formatCurrency(totais.total)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                      }}
                    >
                      <MonetizationOnIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.success.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Comissão Total
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.warning.main }}
                      >
                        {formatCurrency(totais.comissao)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}
                    >
                      <AttachMoneyIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.warning.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* GRAFICOS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              mb: 3,
            }}
          >
            <Grid item xs={12} lg={9} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={2}
                >
                  Evolução de Vendas
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    height: isMobile ? 260 : 360,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <AreaChart data={dadosDiarios}>
                      <defs>
                        <linearGradient
                          id="gradientVendas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0.8}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" />

                      <XAxis dataKey="dia" />

                      <YAxis
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        }
                      />

                      <RechartsTooltip
                        formatter={(value: any) =>
                          Number(value).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        }
                      />

                      <Legend />

                      <Area
                        type="monotone"
                        dataKey="valor"
                        name="Vendas"
                        stroke={theme.palette.primary.main}
                        fill="url(#gradientVendas)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* GRAFICO ROSCA */}
            <Grid item xs={12} lg={3} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={2}
                >
                  Tipo de Venda
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <PieChart>
                      <Pie
                        data={dadosRosca}
                        dataKey="value"
                        innerRadius={35}
                        outerRadius={55}
                        paddingAngle={3}
                      >
                        {dadosRosca.map((item, index) => (
                          <Cell
                            key={index}
                            fill={item.color}
                          />
                        ))}
                      </Pie>

                      <RechartsTooltip
                        formatter={(value: any) =>
                          Number(value).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        }
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box mt={1}>
                  {dadosRosca.map((item) => (
                    <Box
                      key={item.name}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: item.color,
                          }}
                        />

                        <Typography variant="caption">
                          {item.name}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        fontWeight="bold"
                      >
                        {formatCurrency(item.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* VENDEDORES */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={2}
                  pb={1}
                  borderBottom={`2px solid ${alpha(theme.palette.primary.main, 0.2)}`}
                >
                  <GroupsIcon color="primary" />

                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ flex: 1 }}
                  >
                    Vendedores
                  </Typography>

                  <Chip
                    label={`${vendedores.length} vendedores`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <TableContainer
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Vendedor
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Qtd. Vendas
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Valor Total
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Comissão
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vendedores
                        .slice()
                        .sort((a, b) => b.total - a.total)
                        .map((item) => (
                          <TableRow
                            key={item.nome}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                ),
                                transition:
                                  'background-color 0.2s',
                              },
                            }}
                          >
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                <PersonIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'text.secondary',
                                  }}
                                />

                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                >
                                  {item.nome}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell align="right">
                              <Chip
                                label={item.vendas}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>

                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="success.main"
                              >
                                {formatCurrency(item.total)}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="warning.main"
                              >
                                {formatCurrency(item.comissao)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {vendedores.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum vendedor encontrado
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </LayoutBaseDeDashboard>
  );
};
